import { Card, wantsCardImage } from '@lingoping/core';
import { DEFAULT_SUPABASE_URL } from '../config/supabase.js';

type ImageFields = Pick<Card, 'lemma' | 'front_text' | 'image_url' | 'part_of_speech'>;

/** Bucket and folder of the Basic German pictures (the seed script uploads them as de/deck/<slug>.jpg). */
const DECK_IMAGE_PATH = 'storage/v1/object/public/card-images/de/deck';

/** The public URL of a Basic German picture in storage. */
export function storedDeckImageUrl(slug: string, supabaseUrl: string = DEFAULT_SUPABASE_URL) {
  return `${supabaseUrl.replace(/\/+$/, '')}/${DECK_IMAGE_PATH}/${slug}.jpg`;
}

/** `bundled:<deck>/<deck>-<slug>.jpg`, the reference older versions stored for packaged pictures. */
const BUNDLED_REFERENCE = /^bundled:([^/]+)\/([^/]+)\.jpg$/;

/** The picture slug a `bundled:` reference stands for ("bundled:de-basic/de-basic-tisch.jpg" -> "tisch"). */
export function bundledReferenceSlug(url: string): string | undefined {
  const match = BUNDLED_REFERENCE.exec(url);
  if (!match) return undefined;
  const [, deck, file] = match;
  return file.startsWith(`${deck}-`) ? file.slice(deck.length + 1) : file;
}

/**
 * The slug the seed script gives a Basic German word's picture: the lemma lower-cased, umlauts
 * and ß spelled out, everything else that is not a letter or digit as one hyphen.
 */
export function deckImageSlug(lemma: string): string {
  return lemma
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Turns a stored image reference into a loadable URL. Pictures of the Basic German deck used to
 * ship inside the extension (`bundled:` references); they live in storage now, so a card that was
 * not migrated yet (see migrateBundledPictures) still shows its picture from there.
 */
export function resolveImageUrl(url: string): string {
  const slug = bundledReferenceSlug(url);
  return slug ? storedDeckImageUrl(slug) : url;
}

/** Old cards point at the retired on-the-fly image service; those images are regenerated. */
function isLegacyImageUrl(url: string): boolean {
  return url.startsWith('https://image.pollinations.ai/');
}

function storedImage(card: ImageFields): string | undefined {
  if (!wantsCardImage(card.part_of_speech)) return undefined;
  return card.image_url && !isLegacyImageUrl(card.image_url) ? card.image_url : undefined;
}

/** Whether a noun/verb card already has its picture (from the library or generated). */
export function hasUsableImage(card: ImageFields): boolean {
  return storedImage(card) !== undefined;
}

/** The card's picture. Nouns and verbs only. */
export function cardImageUrl(card: ImageFields): string | undefined {
  const url = storedImage(card);
  return url ? resolveImageUrl(url) : undefined;
}
